import React, { useMemo } from 'react';
import { Col, Row } from 'reactstrap';
import { Form } from 'formik';
import * as PropTypes from 'prop-types';
import FormSelectField from '../../common/components/formFields/FormSelectField';
import { constantToSelect } from '../../common/helpers/constantToSelect';
import { TyreDepths } from '../../constants/TyreDepths';
import AddVehicleContentWrapper from '../AddVehicleContentWrapper';
import SettingsSectionHeader from '../../settings/SettingsSectionHeader';
import '../../assets/css/componentStyles/FormValidation.css';

function TyreTreadDepths({
  form,
  title = 'Tyres',
  description = 'Add specific tyre tread depths.',
  className = '',
  maxMenuHeight = 150,
  labelVariant = 'bordered',
  validationErrors = {},
  onValidate,
}) {
  const tyreDepths = useMemo(() => constantToSelect(TyreDepths), []);

  const getServerError = (fieldName) => {
    const allErrors = validationErrors?.TYRES;
    if (!allErrors) return null;

    // If the error is an object, map to the specific field
    if (typeof allErrors === 'object' && allErrors !== null) {
      return allErrors[fieldName] || null;
    }
    return null;
  };

  const getFieldError = (fieldName) => {
    if (!validationErrors || !validationErrors.TYRES) return null;

    const fieldMappings = {
      'tyreThreadDepths.nearsideFront': 'Nearside Front',
      'tyreThreadDepths.offsideFront': 'Offside Front',
      'tyreThreadDepths.nearsideRear': 'Nearside Rear',
      'tyreThreadDepths.offsideRear': 'Offside Rear',
    };

    const displayName = fieldMappings[fieldName];
    return displayName ? validationErrors.TYRES[displayName] : null;
  };

  const tyrePositions = useMemo(
    () => [
      {
        name: 'tyreThreadDepths.nearsideFront',
        label: 'Nearside front (mm)',
        ariaLabel: 'Select nearside front tyre tread depth in millimeters',
      },
      {
        name: 'tyreThreadDepths.offsideFront',
        label: 'Offside front (mm)',
        ariaLabel: 'Select offside front tyre tread depth in millimeters',
      },
      {
        name: 'tyreThreadDepths.nearsideRear',
        label: 'Nearside rear (mm)',
        ariaLabel: 'Select nearside rear tyre tread depth in millimeters',
      },
      {
        name: 'tyreThreadDepths.offsideRear',
        label: 'Offside rear (mm)',
        ariaLabel: 'Select offside rear tyre tread depth in millimeters',
      },
    ],
    [],
  );

  const fieldSelectProps = useMemo(
    () => ({
      maxMenuHeight,
    }),
    [maxMenuHeight],
  );

  const headerContent = useMemo(
    () => ({
      title,
      description,
    }),
    [title, description],
  );

  const generalServerError = validationErrors?.TYRES?.tyreThreadDepths || null;

  return (
    <div onBlur={onValidate}>
      <AddVehicleContentWrapper className={className}>
        <SettingsSectionHeader
          header={headerContent.title}
          description={headerContent.description}
        />
        {typeof generalServerError === 'string' &&
          !form.touched?.tyreThreadDepths?.nearsideFront &&
          !form.touched?.tyreThreadDepths?.offsideFront &&
          !form.touched?.tyreThreadDepths?.nearsideRear &&
          !form.touched?.tyreThreadDepths?.offsideRear && (
            <div className="field-error-message w-100 mt-3">{generalServerError}</div>
          )}
        <Row>
          <Col md={12} className="justify-content-center mr-auto ml-auto">
            <Form className="add-tyres-form">
              <Row>
                {tyrePositions.map((position) => {
                  const tyresError = getFieldError(position.name);
                  const serverError = getServerError(position.name);
                  const fieldNameKey = position.name.split('.')[1];
                  const isTouched = form.touched?.tyreThreadDepths?.[fieldNameKey];
                  const hasValue = form.values?.tyreThreadDepths?.[fieldNameKey];

                  const shouldHideError = isTouched || hasValue;

                  return (
                    <Col key={position.name} sm={12} md={3} className="mb-3 mb-md-0">
                      <FormSelectField
                        name={position.name}
                        label={position.label}
                        options={tyreDepths}
                        className="w-100"
                        rowClassNames="w-100"
                        required
                        selectProps={{
                          ...fieldSelectProps,
                          'aria-label': position.ariaLabel,
                        }}
                        labelVariant={labelVariant}
                        form={form}
                        blurInputOnSelect={false}
                      />
                      {tyresError && !serverError && !shouldHideError && (
                        <div className="field-error-message w-100">{tyresError}</div>
                      )}
                      {serverError && !shouldHideError && (
                        <div className="field-error-message w-100">{serverError}</div>
                      )}
                    </Col>
                  );
                })}
              </Row>
            </Form>
          </Col>
        </Row>
      </AddVehicleContentWrapper>
    </div>
  );
}

TyreTreadDepths.propTypes = {
  form: PropTypes.shape({
    setFieldValue: PropTypes.func.isRequired,
    values: PropTypes.object,
    errors: PropTypes.object,
    touched: PropTypes.object,
  }).isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  className: PropTypes.string,
  maxMenuHeight: PropTypes.number,
  labelVariant: PropTypes.oneOf(['default', 'bordered', 'floating']),
  validationErrors: PropTypes.object,
  onValidate: PropTypes.func,
};

TyreTreadDepths.defaultProps = {
  title: 'Tyres',
  description: 'Add specific tyre tread depths.',
  className: '',
  maxMenuHeight: 150,
  labelVariant: 'bordered',
  validationErrors: {},
  onValidate: () => {},
};

export default TyreTreadDepths;
